import { useEffect, useState } from "react";
import AddIconButton from "../components/AddIconButton";
import Button from "../components/Button";
import DeleteIconButton from "../components/DeleteIconButton";

const SORT_STORAGE_KEY = "selectDates.sortOptions";

const DEFAULT_SORTS = [
    { id: crypto.randomUUID(), field: "startDate", direction: "ASC" },
    { id: crypto.randomUUID(), field: "duration", direction: "ASC" }
];

const FIELDS = [
    { value: "startDate", label: "Start Date" },
    { value: "endDate", label: "End Date" },
    { value: "duration", label: "Duration" },
    { value: "background", label: "Background Color" },
    { value: "border", label: "Border Color" },
    { value: "text", label: "Text Color" },
    { value: "note", label: "Note" }
];

const loadSortOptions = () => {
    try {
        const raw = localStorage.getItem(SORT_STORAGE_KEY);
        if (!raw) {
            return null;
        }

        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : null;
    } catch {
        return null;
    }
};

const saveSortOptions = (sorts) => {
    try {
        localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(sorts));
    } catch {
        // ignore (storage full / disabled)
    }
};

const DragHandle = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <path
            d="M5 10H19M14 19L12 21L10 19M14 5L12 3L10 5M5 14H19"
            stroke="#000"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const ConfirmSortModal = ({ onConfirm, onCancel }) => {
    //const [sorts, setSorts] = useState(DEFAULT_SORTS);
    const [sorts, setSorts] = useState(() => {
        const stored = loadSortOptions();
        return stored ?? DEFAULT_SORTS;
    });

    useEffect(() => {
        saveSortOptions(sorts);
    }, [sorts]);

    const [dragIndex, setDragIndex] = useState(null);

    const addSort = () => {
        setSorts(prev => [
            ...prev,
            { id: crypto.randomUUID(), field: "startDate", direction: "ASC" }
        ]);
    };

    const removeSort = (id) => {
        setSorts(prev => prev.filter(s => s.id !== id));
    };

    const updateSort = (id, patch) => {
        setSorts(prev =>
            prev.map(s => (s.id === id ? { ...s, ...patch } : s))
        );
    };

    const onDragStart = (index) => {
        setDragIndex(index);
    };

    const onDrop = (index) => {
        if (dragIndex === null || dragIndex === index) {
            return;
        }

        const next = [...sorts];
        const [moved] = next.splice(dragIndex, 1);
        next.splice(index, 0, moved);

        setSorts(next);
        setDragIndex(null);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded p-6 w-[500px] space-y-4">
                <h3 className="text-lg font-semibold">Sort selected items</h3>

                <div className="space-y-2">
                    {sorts.map((sort, index) => (
                        <div
                            key={sort.id}
                            className="flex items-center gap-2 border rounded p-2"
                            draggable
                            onDragStart={() => onDragStart(index)}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => onDrop(index)}
                        >
                            <div className="cursor-move">
                                <DragHandle />
                            </div>

                            <select
                                value={sort.field}
                                onChange={(e) =>
                                    updateSort(sort.id, { field: e.target.value })
                                }
                                className="border rounded p-1"
                            >
                                {FIELDS.map(f => (
                                    <option key={f.value} value={f.value}>
                                        {f.label}
                                    </option>
                                ))}
                            </select>

                            <select
                                value={sort.direction}
                                onChange={(e) =>
                                    updateSort(sort.id, { direction: e.target.value })
                                }
                                className="border rounded p-1"
                            >
                                <option value="ASC">ASC</option>
                                <option value="DESC">DESC</option>
                            </select>

                            <div className="ml-auto">
                                <DeleteIconButton
                                    onClick={() => removeSort(sort.id)}
                                    title="Remove sort option"
                                />
                            </div>
                        </div>
                    ))}
                </div>

                <AddIconButton onClick={addSort} />

                <div className="flex justify-end gap-2 pt-2">
                    <Button label="Cancel" onClick={onCancel} variant="secondary" />
                    <Button
                        label="Confirm"
                        onClick={() => onConfirm(sorts)}
                        variant="primary"
                        disabled={sorts.length === 0}
                    />
                </div>
            </div>
        </div>
    );
};

export default ConfirmSortModal;